import React, {PropsWithChildren, useCallback, useEffect, useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {
    MaterialReactTable,
    MRT_Column,
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
    useMaterialReactTable,
} from "material-react-table";
import {useQuery} from "@tanstack/react-query";
import type {Dayjs} from "dayjs";
import Box from "@mui/material/Box";
import {
    Alert,
    Button,
    FilledInput,
    Link,
    ListItemIcon,
    MenuItem,
    Select,
    SelectProps,
    Stack,
    TextFieldProps,
    Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import PauseIcon from "@mui/icons-material/Pause";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DoneIcon from "@mui/icons-material/Done";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/AddOutlined";
import TextField from "@mui/material/TextField";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TuneIcon from "@mui/icons-material/Tune";
import {Order} from "../../types/domain/order";
import {CompanyFilter} from "../CompanyFilter";
import {OrderBulkCreateDialog} from "../dialogs/OrderBulkCreateDialog";
import {OrderRemoveDialog} from "../dialogs/OrderRemoveDialog";
import {selectedCompanyAtom, selectedOrderAtom} from "../../store/filterSelectionStore";
import {routerCompanyIdAtom} from "../../store/routerStore";
import {OrderStatus} from "../../types/domain/orderStatus";
import {ORDER_STATUS_ORDERED} from "../../utils/orderUtils";
import {OrderChangeStatusDialog} from "../dialogs/OrderChangeStatusDialog";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {FieldsTable} from "../fields/FieldsTable";
import {authenticatedUserLoadable, authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {isUserHaveAnyOfGlobalRoles} from "../../utils/accessControlUtils";
import {UserGlobalRole} from "../../types/domain/user";
import {OrderCreateDialog} from "../dialogs/OrderCreateDialog";
import {useOrderManagement} from "../../hooks/ordersHooks";
import {getResourceErrorMessage, isResourceFailed, isResourceSucceeded} from "../../utils/resourceUtils";
import {OrderFinalizeResponse} from "../../types/orderFinalizeResponse";
import {swallowException} from "../../utils/appUtils";
import {useCrops} from "../../hooks/cropsHooks";
import {useSoils} from "../../hooks/soilsHooks";
import {useIrrigations} from "../../hooks/irrigationsHooks";
import {useCropFilter, useFilter} from "../../hooks/filterHooks";
import {getOrderConfirmPageUrl} from "../../utils/orderUtils";
import {OrderFlagsEditDialog} from "../dialogs/OrderFlagsEditDialog";
import {getOrders} from "../../services/orderService";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {Field} from "../../types/domain/field";
import {QueryFilterItem, TableFilterSelectOption} from "../../types/table";
import {CustomDateRangeFilter} from "./CustomDateRangeFilter";
import {PaginatedResponse} from "../../types/PaginatedResponse";
import {AdminOrderFlags} from "./AdminOrderFlags";
import {OrderFlags} from "../../types/domain/orderFlags";
import {debounce} from "lodash";
import {useCompanies} from "../../hooks/companiesHooks";

export interface FilterProps extends PropsWithChildren {
    label: string;
}

const FilterInput: React.FC<FilterProps> = ({children, label}) => (
    <div style={{maxWidth: "12rem", flex: "1 1 15%"}}>
        <Stack direction="row" justifyContent="flex-start" alignItems="center">
            <Typography fontSize={12} textTransform="uppercase" color={"#737990"}>
                {label}
            </Typography>
            <FilterAltIcon fontSize="small" sx={{opacity: 0.87, color: "#737990"}} />
        </Stack>
        {children}
    </div>
);

interface GetOrdersQuery {
    companyId?: QueryFilterItem;
    endDate?: QueryFilterItem;
    state?: QueryFilterItem;
    id?: QueryFilterItem;
    fieldCropId?: QueryFilterItem;
    fieldSoilId?: QueryFilterItem;
    fieldIrrigationTypeId?: QueryFilterItem;
    fieldName?: QueryFilterItem;
    flags?: QueryFilterItem;
}

interface FieldFilter {
    options: string[];
    selectedOption: string;
    setSelectedOption: React.Dispatch<React.SetStateAction<string>>;
}

export const AdminOrdersView: React.FC = () => {
    const {t} = useTranslation();
    const {cropsById} = useCrops();
    const {soilsById} = useSoils();
    const {irrigationsById} = useIrrigations();
    const [fieldNameFilter, setFieldNameFilter] = useState<string>("");
    const [fieldNameInput, setFieldNameInput] = useState<string>("");
    const [userMessage, setUserMessage] = useState<string | null>(null);
    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const isAdmin =
        isResourceSucceeded(authenticatedUserResource) &&
        isUserHaveAnyOfGlobalRoles(authenticatedUserResource.data, [UserGlobalRole.ADMIN, UserGlobalRole.CS]);

    const [selectedCompany] = useAtom(selectedCompanyAtom);
    const setRouterCompanyId = useSetAtom(routerCompanyIdAtom);
    const companies = useCompanies();
    const companyId = selectedCompany ? selectedCompany.id : null;

    useEffect(() => {
        setRouterCompanyId(selectedCompany?.id);
    }, [selectedCompany, setRouterCompanyId]);
    const ordersManagement = useResourceManagement({getOrders});
    const [selectedOrder, setSelectedOrder] = useAtom(selectedOrderAtom);
    const [globalFields, setGlobalFields] = useState<Field[]>([]);
    const cropFilter = useCropFilter();
    const soilFilter = useFilter(globalFields, "soilId");
    const irrigationFilter = useFilter(globalFields, "irrigationTypeId");
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

    const [orderCreateDialogOpened, setOrderCreateDialogOpened] = useState<boolean>(false);
    const [orderFlagsDialogOpened, setOrderFlagsDialogOpened] = useState<boolean>(false);
    const [orderEditDialogOpened, setOrderEditDialogOpened] = useState<boolean>(false);
    const [changeOrderStatusOpened, setChangeOrderStatusOpened] = useState<boolean>(false);
    const [orderRemoveDialogOpened, setOrderRemoveDialogOpened] = useState<boolean>(false);
    const [orderCancelDialogOpened, setOrderCancelDialogOpened] = useState<boolean>(false);
    const [orderPauseDialogOpened, setOrderPauseDialogOpened] = useState<boolean>(false);
    const [orderCompleteDialogOpened, setOrderCompleteDialogOpened] = useState<boolean>(false);
    const orderManagement = useOrderManagement();

    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<Order>(locale);

    const onCompleteDialogClose = (isSuccessful: boolean) => {
        setOrderCompleteDialogOpened(false);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };
    const onPauseDialogClose = (isSuccessful: boolean) => {
        setOrderPauseDialogOpened(false);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };
    const onCancelDialogClose = (isSuccessful: boolean) => {
        setOrderCancelDialogOpened(false);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };

    const onOrderCreateDialogClose = (isSuccessful: boolean) => {
        setOrderCreateDialogOpened(false);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };

    const onOrderEditDialogClose = (isSuccessful: boolean) => {
        setOrderEditDialogOpened(false);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };

    const onChangeStatusDialogClose = (isSuccessful: boolean) => {
        setChangeOrderStatusOpened(false);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };
    const onOrderRemoveDialogClose = (isSuccessful: boolean) => {
        setOrderRemoveDialogOpened(false);
        setSelectedOrder(null);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };

    const onOrderFlagsDialogClose = (isSuccessful: boolean) => {
        setOrderFlagsDialogOpened(false);
        if (isSuccessful) {
            refreshAllOrders();
        }
    };
    const constructUserMessage = (response: OrderFinalizeResponse): string => {
        let message = response.message;
        if (response.failedFields) {
            message = `${message} Not finalized fields: ${response.failedFields}`;
        }
        return message;
    };
    const handleCloseAlert = () => {
        setUserMessage(null);
    };
    const finalizeAll = (order: Order) => {
        if (!order.companyId) {
            return;
        }
        orderManagement
            .finalize(order.companyId, order.id)
            .then((res) => {
                setUserMessage(constructUserMessage(res));
                refreshAllOrders();
            })
            .catch(swallowException);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceFieldName = useCallback(
        debounce((value: string) => setFieldNameFilter(value), 200),
        [setFieldNameFilter],
    );

    const handleFieldNameChange = (value: string) => {
        setFieldNameInput(value);
        debounceFieldName(value);
    };

    const filterPanel = () => {
        const commonInputProps: TextFieldProps = {
            hiddenLabel: true,
            margin: "none",
            size: "small",
            fullWidth: true,
            variant: "filled",
            InputProps: {
                disableUnderline: true,
            },
        };

        const commonSelectProps: SelectProps<string> = {
            margin: "none",
            size: "small",
            fullWidth: true,
            variant: "filled",
            input: <FilledInput disableUnderline hiddenLabel sx={{background: "#E9EBF1"}} />,
        };
        return (
            <Stack
                direction="row"
                alignItems="center"
                useFlexGap
                justifyContent="flex-start"
                spacing={2}
                sx={{
                    width: "100%",
                }}
            >
                <FilterInput label={t("admin.order_list_view.columns.field")}>
                    <TextField
                        {...commonInputProps}
                        value={fieldNameInput}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleFieldNameChange(event.target.value)
                        }
                    ></TextField>
                </FilterInput>
                <FilterInput label={t("admin.order_list_view.columns.crop")}>
                    <Select
                        {...commonSelectProps}
                        onChange={(value) => {
                            cropFilter.setSelectedOption(value.target.value);
                        }}
                        value={cropFilter.selectedOption}
                    >
                        {cropFilter.options.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item === "All" ? "All" : cropsById[item].name}
                            </MenuItem>
                        ))}
                    </Select>
                </FilterInput>
                <FilterInput label={t("admin.order_list_view.columns.soil")}>
                    <Select
                        {...commonSelectProps}
                        onChange={(value) => {
                            soilFilter.setSelectedOption(value.target.value);
                        }}
                        value={soilFilter.selectedOption}
                    >
                        {soilFilter.options.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item === "All" ? "All" : soilsById[item].name}
                            </MenuItem>
                        ))}
                    </Select>
                </FilterInput>
                <FilterInput label={t("admin.order_list_view.columns.irrigation")}>
                    <Select
                        {...commonSelectProps}
                        onChange={(value) => {
                            irrigationFilter.setSelectedOption(value.target.value);
                        }}
                        value={irrigationFilter.selectedOption}
                    >
                        {irrigationFilter.options.map((item) => (
                            <MenuItem key={item} value={item}>
                                {item === "All" ? "All" : irrigationsById[item].name}
                            </MenuItem>
                        ))}
                    </Select>
                </FilterInput>
            </Stack>
        );
    };
    const statusSelectOptions: TableFilterSelectOption[] = [
        {
            value: "",
            label: "All",
        },
        ...Object.values(OrderStatus).map((value) => ({
            value: value,
            label: value,
        })),
    ];

    const flagsSelectOptions: TableFilterSelectOption[] = [
        {
            value: "",
            label: "All",
        },
        ...Object.values(OrderFlags).map((f) => ({label: f, value: f})),
    ];

    const columns: MRT_ColumnDef<Order>[] = [
        {
            id: "sid",
            header: t("admin.order_list_view.columns.sid"),
            accessorKey: "sid",
            Cell: ({renderedCellValue, row}) => (
                <RouterLink to={`/company/${row.original.companyId}/order/${row.original.id}`}>
                    {renderedCellValue}
                </RouterLink>
            ),
            minSize: 30,
            size: 40,
            enableColumnFilter: false,
        },
        {
            id: "id",
            header: t("admin.order_list_view.columns.order"),
            accessorFn: (row) => {
                const id = row.id.slice(0, 8);
                return id;
            },
            minSize: 50,
            size: 70,
            enableColumnFilter: true,
        },
        {
            id: "companyId",
            header: t("admin.order_list_view.columns.company"),
            accessorFn: (row) => {
                return (companies.isSuccess ? companies.data : []).find((company) => company.id === row.companyId)
                    ?.properties.name;
            },
            minSize: 50,
            size: 70,
            enableColumnFilter: false,
        },
        {
            id: "state",
            header: t("admin.order_list_view.columns.status"),
            accessorKey: "state",
            minSize: 50,
            size: 70,
            Cell: ({row}) => {
                const status = row.original.state;
                const orderId = row.original.id;
                const companyId = row.original.companyId;

                if (status === OrderStatus.AWAITING_CONFIRMATION) {
                    return (
                        <Link
                            component={RouterLink}
                            to={getOrderConfirmPageUrl(companyId, orderId)}
                            style={{textDecoration: "none"}}
                        >
                            {status}
                        </Link>
                    );
                } else {
                    return <span>{status}</span>;
                }
            },
            enableColumnFilter: true,
            filterVariant: "select",
            filterSelectOptions: statusSelectOptions,
        },
        {
            id: "startDate",
            header: t("admin.order_list_view.columns.start_date"),
            accessorKey: "startDate",
            minSize: 50,
            size: 70,
            enableColumnFilter: false,
        },
        {
            id: "endDate",
            header: t("admin.order_list_view.columns.end_date"),
            accessorKey: "endDate",
            minSize: 70,
            size: 100,
            enableColumnFilter: true,
            Filter: (props: {column: MRT_Column<Order, unknown>}) => <CustomDateRangeFilter {...props} t={t} />,
        },
        {
            id: "areaInHa",
            header: t("admin.order_list_view.columns.area"),
            accessorKey: "areaInHa",
            minSize: 50,
            size: 70,
            enableColumnFilter: false,
        },
        {
            id: "price",
            header: t("admin.order_list_view.columns.price"),
            accessorKey: "price",
            minSize: 50,
            size: 70,
            enableColumnFilter: false,
        },
        {
            id: "discount",
            header: t("admin.order_list_view.columns.discount"),
            accessorKey: "discount",
            accessorFn: (row) => {
                return row.discount;
            },
            minSize: 40,
            size: 50,
            enableColumnFilter: false,
        },
        {
            id: "finalPrice",
            header: t("admin.order_list_view.columns.final_price"),
            accessorKey: "finalPrice",
            minSize: 50,
            size: 70,
            enableColumnFilter: false,
        },
        {
            id: "flags",
            header: t("admin.order_list_view.columns.flags"),
            accessorKey: "flags",
            minSize: 50,
            size: 70,
            enableColumnFilter: true,
            Cell: ({row}) => <AdminOrderFlags flags={row.original.flags} />,
            filterVariant: "select",
            filterSelectOptions: flagsSelectOptions,
        },
    ];

    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });

    const {
        data: {data = [], meta} = {},
        isError,
        isRefetching,
        isLoading,
        refetch,
    } = useQuery<PaginatedResponse<Order>>({
        queryKey: [
            "table-data",
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
            columnFilters,
            soilFilter.selectedOption,
            cropFilter.selectedOption,
            irrigationFilter.selectedOption,
            fieldNameFilter,
        ],
        queryFn: async () => {
            const fetchURL = new URLSearchParams();
            fetchURL.set("page", `${pagination.pageIndex}`);
            fetchURL.set("size", `${pagination.pageSize}`);
            const sortingArray = sorting.map((item) => (item.desc ? `-${item.id}` : `+${item.id}`));
            sortingArray.forEach((sortParam) => {
                fetchURL.append("sorting[]", sortParam);
            });
            const query: GetOrdersQuery = {};
            if (companyId) {
                query.companyId = {
                    v: companyId,
                    op: "eq",
                };
            }
            for (const f of columnFilters) {
                if (f.id === "endDate") {
                    if (f.value !== "") {
                        const s = (f.value as Dayjs[])[0].format("YYYY-MM-DD");
                        const e = (f.value as Dayjs[])[1].format("YYYY-MM-DD");
                        query.endDate = {
                            v: [s, e],
                            op: "between",
                        };
                    } else {
                        query.endDate = undefined;
                    }
                }
                if (f.id === "state") {
                    query.state = {
                        v: f.value as string,
                        op: "eq",
                    };
                }
                if (f.id === "id") {
                    query.id = {
                        v: f.value as string,
                        op: "like",
                    };
                }
                if (f.id === "flags") {
                    query.flags = {
                        v: f.value as string,
                        op: "contains",
                    };
                }
            }
            addFieldFiltersToQuery(query, soilFilter, cropFilter, fieldNameFilter, irrigationFilter);
            fetchURL.set("query", encodeURIComponent(JSON.stringify(query)));
            const orders = await ordersManagement.getOrders(fetchURL);
            const fields = orders.data.flatMap((o) => o.fields).filter((o) => o);
            setGlobalFields(fields);
            return orders;
        },
        placeholderData: undefined,
    });

    const refreshAllOrders = useCallback(() => {
        void refetch();
    }, [refetch]);
    useEffect(() => {
        refreshAllOrders();
    }, [companyId, refreshAllOrders]);
    const ordersTable = useMaterialReactTable({
        ...defaultTableOptions, //spread your default options
        columns,
        data: data,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,

        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        rowCount: meta?.totalRowCount ?? 0,

        enableGlobalFilter: true,
        enableTopToolbar: true,
        enableMultiSort: true,
        initialState: {
            ...defaultTableOptions.initialState, //spread default initial state
            showColumnFilters: true, //override default initial state for just this table
        },
        state: {
            columnFilters,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            sorting,
        },
        muiDetailPanelProps: () => ({
            sx: {
                padding: "0 !important",
            },
        }),
        renderTopToolbarCustomActions: () => <>{filterPanel()}</>,

        renderDetailPanel: ({row}) => {
            return (
                <FieldsTable
                    fields={row.original.fields.sort((a, b) => a.name.localeCompare(b.name))}
                    refresh={refreshAllOrders}
                    companyId={row.original.companyId}
                    isAdmin={isAdmin}
                />
            );
        },
        renderRowActionMenuItems: ({closeMenu, row}) => [
            <MenuItem
                key={0}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={() => {
                    finalizeAll(row.original);
                    closeMenu();
                }}
            >
                <ListItemIcon>
                    <DoneIcon />
                </ListItemIcon>
                {t("admin.order_list_view.menu.finalize_all")}
            </MenuItem>,
            canOrderTransitionTo(row.original.state, OrderStatus.PAUSED) && (
                <MenuItem
                    key={1}
                    onClick={() => {
                        setSelectedOrder(row.original);
                        setOrderPauseDialogOpened(true);
                        closeMenu();
                    }}
                    sx={{m: 0}}
                >
                    <ListItemIcon>
                        <PauseIcon />
                    </ListItemIcon>
                    {t("admin.order_list_view.menu.pause")}
                </MenuItem>
            ),
            canOrderTransitionTo(row.original.state, OrderStatus.CANCELED) && (
                <MenuItem
                    key={2}
                    onClick={() => {
                        setSelectedOrder(row.original);
                        setOrderCancelDialogOpened(true);
                        closeMenu();
                    }}
                    sx={{m: 0}}
                >
                    <ListItemIcon>
                        <ClearIcon />
                    </ListItemIcon>
                    {t("admin.order_list_view.menu.cancel")}
                </MenuItem>
            ),
            canOrderTransitionTo(row.original.state, OrderStatus.COMPLETED) && (
                <MenuItem
                    key={3}
                    onClick={() => {
                        setSelectedOrder(row.original);
                        setOrderCompleteDialogOpened(true);
                        closeMenu();
                    }}
                    sx={{m: 0}}
                >
                    <ListItemIcon>
                        <DoneAllIcon />
                    </ListItemIcon>
                    {t("admin.order_list_view.menu.complete")}
                </MenuItem>
            ),
            <MenuItem
                key={4}
                onClick={() => {
                    setSelectedOrder(row.original);
                    setOrderEditDialogOpened(true);
                    closeMenu();
                }}
                sx={{m: 0}}
            >
                <ListItemIcon>
                    <EditOutlinedIcon />
                </ListItemIcon>
                {t("admin.order_list_view.menu.edit")}
            </MenuItem>,
            showOrderStatus(row.original.state) && (
                <MenuItem
                    key={5}
                    onClick={() => {
                        setSelectedOrder(row.original);
                        setChangeOrderStatusOpened(true);
                        closeMenu();
                    }}
                    sx={{m: 0}}
                >
                    <ListItemIcon>
                        <TrendingFlatIcon />
                    </ListItemIcon>
                    {t("admin.order_list_view.menu.move_to", {
                        state: getNextOrderStatus(row.original.state),
                    })}
                </MenuItem>
            ),
            <MenuItem
                key={6}
                onClick={() => {
                    setSelectedOrder(row.original);
                    setOrderFlagsDialogOpened(true);
                    closeMenu();
                }}
                sx={{m: 0}}
            >
                <ListItemIcon>
                    <TuneIcon />
                </ListItemIcon>
                {t("admin.order_list_view.menu.order_flags")}
            </MenuItem>,
            <MenuItem
                key={7}
                onClick={() => {
                    setSelectedOrder(row.original);
                    setOrderRemoveDialogOpened(true);
                    closeMenu();
                }}
                sx={{m: 0}}
            >
                <ListItemIcon>
                    <DeleteIcon />
                </ListItemIcon>
                {t("admin.order_list_view.menu.delete")}
            </MenuItem>,
        ],
    });
    return (
        <Stack paddingX="1.5rem" paddingTop="1.5rem" paddingBottom="0.5rem">
            <CompanyFilter allowAll={true} disableAutoSelect={true}></CompanyFilter>

            <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                    {t("admin.order_list_view.title")}
                </Typography>
                <Stack direction="row">
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => setOrderCreateDialogOpened(true)}
                        disabled={!selectedCompany}
                    >
                        {t("admin.order_list_view.new_order_button")}
                    </Button>
                </Stack>
            </Stack>
            {isResourceFailed(orderManagement.resource) && (
                <Alert severity="error" sx={{marginBottom: "1rem"}}>
                    {getResourceErrorMessage(orderManagement.resource)}
                </Alert>
            )}
            {userMessage && (
                <Alert severity="info" sx={{marginBottom: "1rem"}} onClose={handleCloseAlert}>
                    {userMessage}
                </Alert>
            )}
            <Box sx={{overflow: "hidden", borderRadius: 1, bgcolor: "white", mt: "1.25rem", pt: "0.5rem"}}>
                <MaterialReactTable table={ordersTable} />
            </Box>
            {orderCreateDialogOpened && selectedCompany && (
                <OrderBulkCreateDialog
                    open={orderCreateDialogOpened}
                    onClose={onOrderCreateDialogClose}
                    companyId={selectedCompany.id}
                />
            )}
            {orderEditDialogOpened && selectedOrder && (
                <OrderCreateDialog
                    mode="edit"
                    companyId={selectedOrder.companyId}
                    selectedOrder={selectedOrder}
                    open={orderEditDialogOpened}
                    onClose={onOrderEditDialogClose}
                />
            )}
            {orderCompleteDialogOpened && selectedOrder && (
                <OrderChangeStatusDialog
                    companyId={selectedOrder.companyId}
                    orderId={selectedOrder.id}
                    newStatus={OrderStatus.COMPLETED}
                    open={orderCompleteDialogOpened}
                    onClose={onCompleteDialogClose}
                />
            )}
            {orderPauseDialogOpened && selectedOrder && (
                <OrderChangeStatusDialog
                    companyId={selectedOrder.companyId}
                    orderId={selectedOrder.id}
                    newStatus={OrderStatus.PAUSED}
                    open={orderPauseDialogOpened}
                    onClose={onPauseDialogClose}
                />
            )}
            {orderCancelDialogOpened && selectedOrder && (
                <OrderChangeStatusDialog
                    companyId={selectedOrder.companyId}
                    orderId={selectedOrder.id}
                    newStatus={OrderStatus.CANCELED}
                    open={orderCancelDialogOpened}
                    onClose={onCancelDialogClose}
                />
            )}
            {changeOrderStatusOpened && selectedOrder && (
                <OrderChangeStatusDialog
                    companyId={selectedOrder.companyId}
                    orderId={selectedOrder.id}
                    newStatus={getNextOrderStatus(selectedOrder.state)}
                    open={changeOrderStatusOpened}
                    onClose={onChangeStatusDialogClose}
                />
            )}
            {orderFlagsDialogOpened && selectedOrder && (
                <OrderFlagsEditDialog
                    companyId={selectedOrder.companyId}
                    order={selectedOrder}
                    open={orderFlagsDialogOpened}
                    onClose={onOrderFlagsDialogClose}
                />
            )}
            {orderRemoveDialogOpened && selectedOrder && (
                <OrderRemoveDialog
                    companyId={selectedOrder.companyId}
                    order={selectedOrder}
                    open={orderRemoveDialogOpened}
                    onClose={onOrderRemoveDialogClose}
                />
            )}
        </Stack>
    );
};

const addFieldFiltersToQuery = (
    query: GetOrdersQuery,
    soilFilter: FieldFilter,
    cropFilter: FieldFilter,
    fieldNameFilter: string,
    irrigationFilter: FieldFilter,
) => {
    if (soilFilter.selectedOption !== "All") {
        query.fieldSoilId = {
            v: soilFilter.selectedOption,
            op: "eq",
        };
    }
    if (cropFilter.selectedOption !== "All") {
        query.fieldCropId = {
            v: cropFilter.selectedOption,
            op: "eq",
        };
    }
    if (fieldNameFilter?.length > 0) {
        query.fieldName = {
            v: fieldNameFilter,
            op: "like",
        };
    }
    if (irrigationFilter.selectedOption !== "All") {
        query.fieldIrrigationTypeId = {
            v: irrigationFilter.selectedOption,
            op: "eq",
        };
    }
};

/**
 * Determines if the "Move to <NextStatus>" menu button should be shown based on the current order status.
 * Hide the menu button "Move to <NextStatus>" for CANCELED, COMPLETED or ACTIVE statuses to prevent displaying "Move to new".
 * @param status - current order status
 * @returns boolean - true if the status should be displayed, otherwise false
 */

const showOrderStatus = (status: OrderStatus): boolean => {
    const hiddenStatuses = new Set([OrderStatus.CANCELED, OrderStatus.ACTIVE]);
    return !hiddenStatuses.has(status);
};

/**
 * Check if an order can transition to a target status to determine button display
 * @param current - Current order status
 * @param target - Target order status
 * @returns boolean - true if the transition is possible, otherwise false
 */
const canOrderTransitionTo = (current: OrderStatus, target: OrderStatus): boolean => {
    if (current === target || current === OrderStatus.CANCELED) {
        return false; // Cannot transition to the same state or from canceled
    }
    switch (target) {
        case OrderStatus.COMPLETED:
            return current === OrderStatus.ACTIVE || current === OrderStatus.PAUSED;
        case OrderStatus.PAUSED:
            return current === OrderStatus.ACTIVE;
        case OrderStatus.CANCELED:
            return true;
        default:
            return false;
    }
};

const getNextOrderStatus = (current: OrderStatus): OrderStatus => {
    if (current === OrderStatus.PAUSED || current === OrderStatus.COMPLETED) {
        return OrderStatus.ACTIVE;
    }
    const index = ORDER_STATUS_ORDERED.indexOf(current);
    if (index === -1) {
        return ORDER_STATUS_ORDERED[0];
    }
    if (index === ORDER_STATUS_ORDERED.length - 1) {
        return ORDER_STATUS_ORDERED[index];
    }
    return ORDER_STATUS_ORDERED[index + 1];
};
